// Resolves DynamicValue → concrete value (literal | binding | function call)

import type { DataModel } from '../state/dataModel.ts';
import type { DynamicValue, DataBinding, FunctionCall } from '../types/A2UICommon.types.tsx';
import { isDataBinding, isFunctionCall } from '../types/A2UICommon.types.tsx';
import { resolvePointerPath } from '../utils/jsonPointer.ts';

import { executeFunctionCall, type CustomFunctions } from './functionExecutor.ts';

export type ResolverContext = {
	basePath: string;
	customFunctions?: CustomFunctions;
	dataModel: DataModel;
};

export function getBindingCandidatePaths(basePath: string, bindingPath: string): string[] {
	const primaryPath = resolvePointerPath(basePath, bindingPath);
	if (!basePath || !bindingPath.startsWith('/')) {
		return [primaryPath];
	}

	const contextualPath = resolvePointerPath(basePath, bindingPath.slice(1));
	if (contextualPath === primaryPath) {
		return [primaryPath];
	}

	return [primaryPath, contextualPath];
}

export function resolveBindingValue(
	dataModel: DataModel,
	basePath: string,
	bindingPath: string,
): unknown {
	const candidatePaths = getBindingCandidatePaths(basePath, bindingPath);
	for (const candidatePath of candidatePaths) {
		const value = dataModel.get(candidatePath);
		if (value !== undefined) {
			return value;
		}
	}

	return dataModel.get(candidatePaths[0]);
}

export function resolveDynamicValue(value: DynamicValue, context: ResolverContext): unknown {
	// Literal — return as-is
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return value;
	}

	// Array literal (string[])
	if (Array.isArray(value)) {
		return value;
	}

	// DataBinding — read from data model
	if (isDataBinding(value)) {
		const binding = value as DataBinding;
		return resolveBindingValue(context.dataModel, context.basePath, binding.path);
	}

	// FunctionCall — execute with recursively resolved args
	if (isFunctionCall(value)) {
		const fn = value as FunctionCall;
		const resolvedArgs: Record<string, unknown> = {};
		for (const [key, argValue] of Object.entries(fn.args)) {
			resolvedArgs[key] = resolveDynamicValue(argValue as DynamicValue, context);
		}
		return executeFunctionCall(fn.call, resolvedArgs, context.customFunctions);
	}

	return value;
}

export function resolveDynamicString(value: DynamicValue, context: ResolverContext): string {
	const resolved = resolveDynamicValue(value, context);
	return resolved != null ? String(resolved) : '';
}

export function resolveDynamicNumber(value: DynamicValue, context: ResolverContext): number {
	const resolved = resolveDynamicValue(value, context);
	return typeof resolved === 'number' ? resolved : Number(resolved) || 0;
}

export function resolveDynamicBoolean(value: DynamicValue, context: ResolverContext): boolean {
	const resolved = resolveDynamicValue(value, context);
	return Boolean(resolved);
}

export function resolveDynamicStringList(value: DynamicValue, context: ResolverContext): string[] {
	const resolved = resolveDynamicValue(value, context);
	if (Array.isArray(resolved)) {
		return resolved.map(String);
	}
	if (typeof resolved === 'string') {
		return [resolved];
	}
	return [];
}
